import { memo, useMemo, useEffect, useRef } from "react";
import { Handle, Position, NodeProps, useUpdateNodeInternals } from "reactflow";
import { NodeResizer } from "@reactflow/node-resizer";
import "@reactflow/node-resizer/dist/style.css";
import { ArrowUpRight, Clock } from "lucide-react"; // Added Clock icon
import { getStorageUrl } from "@utils/utils";
import { DestinationNodeData } from "@/types";

import { calculateHandleStyle, groupHandlesByPosition, NODE_DIMENSIONS } from '@utils/nodeHelpers';
import { useNodeResizing } from '@hooks/useNodeResizing';

function DestinationNode({ data, selected, id }: NodeProps<DestinationNodeData>) {
  const updateNodeInternals = useUpdateNodeInternals();
  const nodeContentRef = useRef<HTMLDivElement>(null);
  
  // Calculate minimum height based on content
  const calculateMinHeight = useMemo(() => {
    return (width: number, headerHeight: number) => {
      // Start with vertical padding to ensure content doesn't touch borders
      let calculatedHeight = NODE_DIMENSIONS.VERTICAL_PADDING;
      
      // Add header height (measured or estimated)
      calculatedHeight += headerHeight || NODE_DIMENSIONS.HEADER_CONTENT_ESTIMATED_HEIGHT;
      
      const showLabels = width >= NODE_DIMENSIONS.HIDE_LABELS_WIDTH_THRESHOLD;
  
      if (showLabels) {
        let visibleLabelCount = 0;
        if (data.status && data.statusVisibility !== "hidden") visibleLabelCount++;
        if (data.customLabel && data.customLabelVisibility !== "hidden") visibleLabelCount++;
  
        if (visibleLabelCount > 0) {
          calculatedHeight += NODE_DIMENSIONS.LABEL_ROW_ESTIMATED_HEIGHT; // Base for one row
        }
        // Simple heuristic: if more than 2 labels and width is tight, assume a second row for labels
        if (visibleLabelCount > 2 && width < (NODE_DIMENSIONS.MIN_WIDTH + 60)) { 
          calculatedHeight += NODE_DIMENSIONS.LABEL_ROW_ESTIMATED_HEIGHT;
        }
      }
  
      const notesExist = data.notes && data.notes.trim() !== "" && data.notesVisibility !== "hidden";
      if (notesExist) {
        // If notes could be shown, add their minimum required space.
        calculatedHeight += NODE_DIMENSIONS.NOTES_MIN_VISIBLE_HEIGHT_ADDITION;
      }
      
      return Math.min(calculatedHeight, NODE_DIMENSIONS.MAX_HEIGHT);
    };
  }, [data]);

  // Use the node resizing hook
  const { 
    onResize, 
    onResizeEnd, 
    currentWidth, 
    currentHeight,
    effectiveMinHeight 
  } = useNodeResizing({
    id,
    nodeType: 'destination',
    minWidth: NODE_DIMENSIONS.MIN_WIDTH,
    maxWidth: NODE_DIMENSIONS.MAX_WIDTH,
    minHeight: NODE_DIMENSIONS.MIN_HEIGHT,
    maxHeight: NODE_DIMENSIONS.MAX_HEIGHT,
    contentRef: nodeContentRef,
    calculateMinHeight
  });

  // Effect to update node internals when handles change
  useEffect(() => {
    // No need to check for id here as updateNodeInternals takes it
    // Check if data.handles exists and is an array before triggering update
    if (Array.isArray(data.handles)) {
        updateNodeInternals(id);
    }
    // updateNodeInternals function reference is stable,
    // so it doesn't strictly need to be in the dependency array,
    // but including it is safer if its identity could change (unlikely here).
  }, [id, data.handles, updateNodeInternals]); // Dependencies: id, handles data

  // Determine if a valid, positive impact value exists from the data
  const hasValidLatencyImpact = typeof data.latencyImpact === 'number' && data.latencyImpact > 0;

  // Determine if the user explicitly hid it (default to visible if property doesn't exist or isn't 'hidden')
  const isExplicitlyHidden = data.latencyImpactVisibility === 'hidden';

  // Show the label only if there's a valid positive impact AND it's not explicitly hidden by the user
  const showLatencyImpact = hasValidLatencyImpact && !isExplicitlyHidden;

  // --- Handle Rendering Logic ---
  const handles = useMemo(() => {
    return Array.isArray(data.handles) ? [...data.handles] : [];
  }, [data.handles]);

  // Group handles by position
  const handlesByPosition = useMemo(() => {
    return groupHandlesByPosition(handles);
  }, [handles]);
  // --- End Handle Rendering Logic ---


  return (
    <div
      style={{
        width: currentWidth,
        height: currentHeight,
        position: 'relative',
      }}
      className="node-wrapper"
    >
      {selected && (
        <NodeResizer
          minWidth={NODE_DIMENSIONS.MIN_WIDTH}
          maxWidth={NODE_DIMENSIONS.MAX_WIDTH}
          minHeight={effectiveMinHeight}
          maxHeight={NODE_DIMENSIONS.MAX_HEIGHT}
          isVisible={selected}
          onResize={onResize}
          onResizeEnd={onResizeEnd}
          handleStyle={{
            width: '8px',
            height: '8px',
            backgroundColor: '#22c55e', // green-500
            border: '1px solid white',
            borderRadius: '2px',
          }}
          lineStyle={{
            borderColor: '#22c55e',
            borderWidth: '2px',
          }}
        />
      )}
      
      <div
        className={`
          w-full h-full pl-3 pr-4 py-2 rounded-lg bg-white dark:bg-gray-700 border transition-all cursor-pointer shadow-sm dark:shadow-sm-dark hover:shadow-md dark:hover:shadow-md-dark overflow-hidden
          flex flex-col justify-center
            ${selected
              ? "border-green-500 shadow-green-100 outline outline-4 outline-green-500/20 outline-offset-0"
              : "border-gray-300 dark:border-gray-600"
            }
        `}
      >
        <div ref={nodeContentRef} className="flex items-center gap-2 node-content w-full">
          {data.icon ? (
            <img
              src={getStorageUrl(data.icon)}
              alt={data.label}
              className="h-8 w-8 object-contain"
            />
          ) : (
            <div className="w-8 h-8 rounded-full bg-green-50 text-green-600 flex items-center justify-center flex-shrink-0">
              <ArrowUpRight className="h-4 w-4" />
            </div>
          )}

          <div className="min-w-0 content-container w-full">
            <h3 className="font-medium text-sm text-gray-900 dark:text-gray-100 truncate w-full">
              {data.label}
            </h3>
            <div className="flex flex-wrap items-center gap-x-1 labels-container">
              {/* Status label - only show if visibility is not hidden */}
              {data.status && data.statusVisibility !== "hidden" && (
                <span
                  className="mt-1 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium truncate max-w-full bg-yellow-100 text-yellow-800"
                >
                  {data.status}
                </span>
              )}
              
              {/* Custom Label - only show if visibility is not hidden */}
              {data.customLabel && data.customLabelVisibility !== "hidden" && (
                <span
                  className="mt-1 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium truncate max-w-full bg-blue-100 text-blue-800"
                >
                  {data.customLabel}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Notes - only show if visibility is not hidden and we have notes */}
        {currentHeight >= NODE_DIMENSIONS.HIDE_NOTES_HEIGHT_THRESHOLD && data.notes && data.notes.trim() !== "" && data.notesVisibility !== "hidden" && (
          <div className="mt-2 text-[8px] text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-800/50 px-2 py-1 rounded max-h-[80px] overflow-y-auto whitespace-pre-line">
            {data.notes}
          </div>
        )}

        {/* Latency Impact Display near Handle */}
        {showLatencyImpact && (
          <div
            className="absolute left-[-10px] top-1/2 transform -translate-y-1/2 -translate-x-full flex items-center gap-1 px-1.5 py-0.5 rounded bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300 text-[10px] font-medium whitespace-nowrap shadow-sm"
            title={`Estimated Latency Impact: ${data.latencyImpact}ms`}
          >
            <Clock className="h-2.5 w-2.5" />
            <span>{data.latencyImpact}ms</span>
          </div>
        )}

        {/* --- Handle Rendering --- */}
        {handles && handles.length > 0 &&
          Object.entries(handlesByPosition).map(([_, positionHandles]) =>
            positionHandles.map((handle, index) => (
              <Handle
                key={`${handle.id}-${handle.color}`}
                id={handle.id}
                type={handle.type}
                position={handle.position as Position}
                className={`!w-3 !h-3 !border-2 !border-white dark:!border-gray-700`}
                style={{
                  ...calculateHandleStyle(index, positionHandles.length, handle.position),
                  backgroundColor: handle.color,
                }}
                // isConnectable={handle.isConnectable} // Add if needed
              />
            ))
          )
        }
        {/* --- End Handle Rendering --- */}
      </div>
    </div>
  );
}

export default memo(DestinationNode);
